import { forwardRef, useId, useImperativeHandle, useRef, type KeyboardEvent } from 'react';

import { FIELD_SLOT_CLASSES } from '@/shared/components/ui/field/field.tokens';
import { cn } from '@/shared/utils/cn/cn.util';

import { ScalarFieldIsland } from '../ScalarFieldIsland/ScalarFieldIsland';
import { ScalarHint } from '../ScalarHint/ScalarHint';
import { ScalarShell } from '../ScalarShell/ScalarShell';
import { MONEY_AMOUNT_CLASSES, MONEY_CURRENCY_CLASSES } from '../scalar.tokens';
import { type ScalarHandle } from '../types/scalar-field.types';

import { type MoneyInputProps } from './MoneyInput.types';
import { useMoneyInputLogic } from './useMoneyInputLogic';

/** What one press of each key is worth, as a multiple of `step`. */
const KEY_STEPS: Readonly<Record<string, number>> = {
  ArrowUp: 1,
  ArrowDown: -1,
  PageUp: 10,
  PageDown: -10,
};

/**
 * An amount of money, in a currency, at a precision the caller states.
 *
 * **Not self-naming.** One value control, so the caller's `<Label htmlFor>`
 * names it and this component sets no `aria-label` — an `aria-label` wins over
 * a visible label and hides it from the readers it was meant to serve.
 *
 * The box carries the number and the ISO code; the *rate* — "per million
 * tokens" — is in the hint sentence underneath, never crammed in beside the
 * glyph. `$/1M` next to Arabic reorders under bidi, which is the finding P10.4
 * recorded when the managed-settings rate headers had to be respelled in words.
 */
export const MoneyInput = forwardRef<ScalarHandle, MoneyInputProps>(function MoneyInput(
  {
    id,
    className,
    size,
    label,
    value,
    onValueChange,
    currency,
    perUnit,
    min,
    max,
    decimals,
    step,
    isDisabled = false,
    isReadOnly = false,
    isInvalid = false,
    onBlur,
    'aria-describedby': describedBy,
  },
  ref,
) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Generated, never derived from a literal: four rate fields on one form would
  // otherwise all mint `money-hint`, and every field after the first would be
  // described by the FIRST one's stored value (the defect `PercentInput` records).
  const generatedId = useId();
  const hintId = `${id ?? generatedId}-hint`;

  const view = useMoneyInputLogic({
    label,
    value,
    onValueChange,
    currency,
    perUnit,
    min,
    max,
    decimals,
    step,
    isDisabled,
    isReadOnly,
  });

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    get element() {
      return inputRef.current;
    },
  }));

  return (
    <ScalarShell
      className={className}
      label={label}
      isReadOnly={isReadOnly}
      announcement={view.announcement}
      hint={
        <ScalarHint id={hintId} range={view.rangeHint} warning={view.warningHint}>
          {view.storedHint}
        </ScalarHint>
      }
    >
      <ScalarFieldIsland size={size} isInvalid={isInvalid || view.isOutOfRange}>
        <input
          ref={inputRef}
          id={id}
          // `text` + `inputMode`, not `type="number"`: a number input reports an
          // EMPTY value for an in-progress "0." — so a decimal rate cannot be
          // typed into it at all — and it refuses Arabic-Indic digits in the
          // portal's default language. The spinbutton semantics are supplied
          // explicitly below, exactly as `PercentInput` does.
          type="text"
          inputMode="decimal"
          // The amount is an LTR run in BOTH locales. A lone `-` is a
          // bidi-neutral, so an RTL field renders `-3` as `3-` and the sign reads
          // as trailing punctuation — on the one value where being negative is
          // the whole message. The alignment then has to be converted, because
          // `text-align: start` would resolve against this box rather than the
          // page (see `amountAlign`).
          dir="ltr"
          style={{ textAlign: view.amountAlign }}
          role="spinbutton"
          aria-valuenow={view.amount}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuetext={view.valueText}
          aria-invalid={
            isInvalid || view.isOutOfRange || view.number.isDraftInvalid ? true : undefined
          }
          aria-readonly={isReadOnly ? true : undefined}
          aria-describedby={cn(describedBy, hintId)}
          value={view.number.text}
          disabled={isDisabled}
          readOnly={isReadOnly}
          autoComplete="off"
          onChange={(event) => {
            view.number.setText(event.target.value);
          }}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            const steps = KEY_STEPS[event.key];
            if (steps === undefined) return;
            event.preventDefault();
            view.number.stepBy(event.shiftKey ? steps * 10 : steps);
          }}
          onBlur={() => {
            view.number.reset();
            onBlur?.();
          }}
          className={cn(FIELD_SLOT_CLASSES, MONEY_AMOUNT_CLASSES)}
        />
        {/* `aria-hidden` because the readout already carries the currency in
            words (`aria-valuetext`), and `dir="ltr"` because a code is an
            identifier — `SAR` must not be reordered beside a Latin number. */}
        <span aria-hidden dir="ltr" className={MONEY_CURRENCY_CLASSES}>
          {view.currencyCode}
        </span>
      </ScalarFieldIsland>
    </ScalarShell>
  );
});
